import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  Autocomplete
} from '@mui/material';
import { createMasterForm } from './FormManagementQueries';
import { AnnualReviewFormSchema, ProbationReviewFormSchema } from './FormSchemas';
import { getAllCorporateUsers } from '../CorporateQueries';

const CreateFormDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    form_name: '',
    form_type: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'draft'
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formTypes = [
    { value: 'annual', label: 'Annual Performance Review' },
    { value: 'probation', label: 'Probation Progress Assessment' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'closed', label: 'Closed' }
  ];

  // Get the appropriate schema based on form type
  const getSchemaForType = (type) => {
    if (type === 'annual') {
      return AnnualReviewFormSchema;
    } else if (type === 'probation') {
      return ProbationReviewFormSchema;
    }
    return null;
  };

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const result = await getAllCorporateUsers();
        // getAllCorporateUsers already returns the parsed array
        const usersList = Array.isArray(result) ? result : [];
        // Filter to show only employees with "Employee" role
        const employeeRoleOnly = usersList.filter(user => user.role === 'Employee');
        console.log('[CreateFormDialog] Loaded employees:', employeeRoleOnly);
        setEmployees(employeeRoleOnly);
      } catch (error) {
        console.error('Error loading employees:', error);
        setEmployees([]);
      }
    };

    if (open) {
      loadEmployees();
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.form_name.trim()) {
      setError('Form name is required');
      return false;
    }
    if (!formData.form_type) {
      setError('Form type is required');
      return false;
    }
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return false;
    }
    if (!formData.start_date) {
      setError('Start date is required');
      return false;
    }
    if (!formData.end_date) {
      setError('End date is required');
      return false;
    }
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      setError('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Get the schema for the selected form type
      const schema = getSchemaForType(formData.form_type);
      
      if (!schema) {
        setError('Invalid form type selected');
        setLoading(false);
        return;
      }

      // Store complete employee objects including reporting managers
      const employeeObjects = selectedEmployees.map(emp => ({
        email: emp.email,
        employee_email: emp.email,
        name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email,
        employee_name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email,
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        reporting_l1: emp.reporting_l1 || '',
        reporting_l2: emp.reporting_l2 || ''
      }));

      // Create the form with the schema and assigned employees
      await createMasterForm({
        form_name: formData.form_name,
        form_type: formData.form_type,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        form_schema: schema, // Schema is automatically added based on form type
        assigned_employees: employeeObjects // Assign complete employee objects
      });

      // Reset form and close dialog
      setFormData({
        form_name: '',
        form_type: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'draft'
      });
      setSelectedEmployees([]);
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating form:', err);
      setError('Failed to create form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        form_name: '',
        form_type: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'draft'
      });
      setSelectedEmployees([]);
      setError('');
      onClose();
    }
  };

  // Get selected schema info for display
  const selectedSchema = getSchemaForType(formData.form_type);
  const schemaInfo = selectedSchema ? {
    sections: selectedSchema.sections?.length || 0,
    fields: selectedSchema.sections?.reduce((acc, section) => acc + (section.fields?.length || 0), 0) || 0
  } : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Master Form</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={2.5}>
            {/* Form Name */}
            <TextField
              label="Form Name"
              value={formData.form_name}
              onChange={(e) => handleChange('form_name', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Q1 2026 Annual Review"
              helperText="Enter a descriptive name for this form"
            />

            {/* Form Type */}
            <FormControl fullWidth required>
              <InputLabel>Form Type</InputLabel>
              <Select
                value={formData.form_type}
                onChange={(e) => handleChange('form_type', e.target.value)}
                label="Form Type"
              >
                <MenuItem value="">
                  <em>Select form type</em>
                </MenuItem>
                {formTypes.map((type) => (
                  <MenuItem
                    key={type.value}
                    value={type.value}
                  >
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="When can employees start filling this form?"
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Last date for form submission"
              />
            </Stack>

            {/* Employee Assignment */}
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.email || ''})`}
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign to Employees"
                  placeholder="Start typing to search employees..."
                  required
                  helperText="Select employees who will have access to this form"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.email}
                    label={`${option.first_name} ${option.last_name}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />

            {/* Status */}
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this form"
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFormDialog;
